import { getSessionToken } from '@descope/react-sdk';
import { createApiClient } from '@kirin/api-client/client';
import { handleApiError } from './api-error-handler';
import { shouldTriggerGlobal401Logout } from './api401SessionPolicy';
import { assignLocationAfterAuthFailure } from './lastVisitedPath';
import { attemptTokenRefresh } from './tokenRefreshBridge';

/**
 * API base URL from environment or default to localhost.
 * In Vite (browser), uses `import.meta.env` which is replaced at build time.
 * In Jest (test), the `jest-import-meta-transformer` rewrites `import.meta.env`
 * to `process.env`. We reference `import.meta.env` directly and never a bare
 * `typeof import.meta`, which the transformer can't rewrite and which crashes
 * the CommonJS test build.
 */
export const API_BASE =
  import.meta.env?.VITE_API_BASE ||
  (typeof process !== 'undefined' && process.env?.VITE_API_BASE) ||
  'http://localhost:13722';

/**
 * Get authentication token from Descope session
 */
async function getToken(): Promise<string | null> {
  try {
    return getSessionToken();
  } catch (error) {
    console.warn('Failed to get session token:', error);
    return null;
  }
}

/**
 * Create API client instance.
 *
 * `onUnauthorized` delegates to the token-refresh bridge so the shared
 * interceptor can silently refresh the Descope session and retry the
 * request before any destructive logout/reload occurs.
 */
export const apiClient = createApiClient({
  baseURL: `${API_BASE}/api`,
  getToken,
  onError: handleApiError,
  onUnauthorized: attemptTokenRefresh,
  timeout: 30000,
});

/**
 * Last-resort 401 handler. Any 401 that reaches this interceptor has already
 * been through the shared interceptor's refresh-and-retry logic (which failed
 * or was unavailable). Only then do we clear the Descope blob and hard-navigate.
 *
 * Do not clear `lastVisitedPath` here first; the redirect needs it for
 * post-login return. (`TokenManagerProvider` clears before assign on SDK
 * logout paths.)
 *
 * When a refreshed retry itself returns 401, `client.request()` sends the
 * retry through all interceptors (including this one), then its rejection
 * propagates back through the original request's chain — hitting this
 * interceptor a second time with the same error reference. The WeakSet
 * ensures the cleanup side-effects run exactly once per error.
 */
const handled401Errors = new WeakSet<object>();

apiClient.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status ?? error?.status;

    if (
      status === 401 &&
      error &&
      typeof error === 'object' &&
      !handled401Errors.has(error) &&
      shouldTriggerGlobal401Logout(error)
    ) {
      handled401Errors.add(error);
      console.warn('Authentication failed after token refresh, logging out');
      localStorage.removeItem('DS');
      assignLocationAfterAuthFailure();
    }

    return Promise.reject(error);
  }
);

export default apiClient;
